// ---------------------------------------------------------------------------
// >>> **IT #212 — "HOW DO I ADD SOMEONE TO MY CIRCLE?"** <<<
//
// Her words, 2026-09-08: *"How do I add another person to an akashic Records reading? When I click
// on 'someone new' it doesn't give me an option to enter their information. It says 'choose someone
// from your circle.' How do I add someone to my circle?"*
//
// **SHE WAS NOT DOING IT WRONG. THERE WAS NO DOOR.** The Akashic two-person readings render their
// second picker with `allowNew: false`, so the "Someone new" chip is not drawn at all on that seat —
// and the flow needs a PERSON ID for the second subject, which a fresh unsaved subject does not
// have. So the room asked her for somebody from her circle and gave her no way to put anybody in it.
//
// This is that door, and it is deliberately the ADD half of `PersonEditModal` rather than a new kind
// of form: the same `CreateRoomForm`, the same name law, the same 12-hour/24-hour trap already paid
// for once in this house. **The only difference is `addPerson` instead of `editPerson`** — because a
// second form for entering a birth would be a second answer to what a birth is (R28/R29).
//
// The person lands in her circle, so the second seat can pick them AND every other reading in the
// house can use them afterwards. That is the right shape: she asked to add somebody to her circle,
// not to smuggle one person into one reading.
// ---------------------------------------------------------------------------
import { useState, type FormEvent } from "react";
import { CreateRoomForm } from "./CreateRoomForm";
import { ModalBackdrop } from "./ModalBackdrop";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { combineName } from "../nameParts.js";
import { addPerson, personNote } from "../lib/people";
import { initialBirthChart, type BirthChartForm } from "./BirthFields";

export function PersonAddModal({ onClose, onSaved, language = DEFAULT_LANGUAGE, heading }: {
  onClose: () => void;
  onSaved: () => void;
  language?: string;
  heading?: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => setForm((f) => ({ ...f, [field]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const name = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    if (!name.trim()) { setErr(t(language, "subject.editNameRequired")); return; }
    setSaving(true); setErr("");
    try {
      const answer = await addPerson({ ...form, name });
      if (!answer.ok) {
        setErr(personNote(answer, { signIn: t(language, "subject.editSignIn"), failed: t(language, "common.saveFailed") }));
        setSaving(false);
        return;
      }
      onSaved();
    } catch { setErr(t(language, "common.saveFailed")); setSaving(false); }
  }

  const title = heading || "Add someone to your circle";
  return (
    <ModalBackdrop label={title} className="person-edit-modal">
      <div className="person-edit">
        <div className="person-edit__head">
          <h2>{title}</h2>
          <button type="button" className="person-edit__close" onClick={onClose} aria-label={t(language, "common.close")}>×</button>
        </div>
        <CreateRoomForm birthChart={form} updateBirthChart={update} onSubmit={submit} language={language}
          submitLabel={saving ? t(language, "result.saving") : "Add to my circle"}
          nameHint={t(language, "subject.editHint")} generating={saving} />
        {err ? <p className="person-edit__err">{err}</p> : null}
      </div>
    </ModalBackdrop>
  );
}
